import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IoChevronBack } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { TERMS_TYPES, getAllTerms, updateTerm, listTermVersions } from '../../../services/termsService';

const formatDateTime = (val) => {
  if (!val) return '-';
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '-';
  }
};

const TermsSettings = () => {
  const navigate = useNavigate();
  const [allTerms, setAllTerms] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState('');
  const [historyTerm, setHistoryTerm] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState('');

  const loadAll = async () => {
    const data = await getAllTerms();
    setAllTerms(data);
    const next = {};
    TERMS_TYPES.forEach((t) => {
      if (data[t.key]) {
        next[t.key] = {
          version: data[t.key].version || '',
          effective_date: data[t.key].effective_date || '',
          content: data[t.key].content || '',
        };
      }
    });
    setDrafts(next);
  };

  useEffect(() => { loadAll(); }, []);

  const patchDraft = (key, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const openHistory = async (t) => {
    setHistoryTerm(t);
    setExpandedHistoryId('');
    setHistoryLoading(true);
    try {
      const rows = await listTermVersions(t.key, { limit: 50 });
      setHistoryRows(rows);
    } catch (e) {
      // Firestore 인덱스 에러는 콘솔에 그대로 찍어 — 메시지에 콘솔 생성 링크 포함
      console.error('[버전 이력 조회 실패]', e);
      alert(e.message || '버전 이력 조회 실패');
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryTerm(null);
    setHistoryRows([]);
    setExpandedHistoryId('');
  };

  const handleSaveOne = async (t) => {
    const draft = drafts[t.key];
    if (!draft) return;
    if (!draft.version.trim() || !draft.effective_date) {
      alert(`${t.label} — 버전/시행일을 확인해 주세요.`);
      return;
    }
    const prevVersion = allTerms[t.key]?.version || '';
    if (prevVersion && draft.version.trim() !== prevVersion) {
      const ok = window.confirm(
        `버전이 ${prevVersion} → ${draft.version.trim()} 로 변경됩니다.\n\n` +
        `저장하면 해당 약관이 필수인 모든 회원이 다음 로그인 시 재동의 화면(/consent)으로 이동합니다.\n계속하시겠습니까?`,
      );
      if (!ok) return;
    }
    setSavingKey(t.key);
    try {
      await updateTerm(t.key, {
        title: allTerms[t.key]?.title,
        version: draft.version.trim(),
        effective_date: draft.effective_date,
        content: draft.content,
      });
      alert(`${t.label} 저장 완료`);
      await loadAll();
    } catch (e) {
      alert(e.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <Wrap>
      <Header>
        <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={20} /> 뒤로가기</BackBtn>
        <Title>약관 및 정책 관리</Title>
        <Desc>각 약관을 블록별로 수정·저장합니다.</Desc>
      </Header>

      {TERMS_TYPES.map((t) => {
        const current = allTerms[t.key];
        const draft = drafts[t.key] || { version: '', effective_date: '', content: '' };
        const readOnly = !t.editable;
        return (
          <Card key={t.key}>
            <CardHeader>
              <CardTitle>{t.label}</CardTitle>
              <CardHeadActions>
                {!readOnly && (
                  <HistoryBtn type="button" onClick={() => openHistory(t)}>버전 이력</HistoryBtn>
                )}
                {readOnly && <ReadOnlyChip>수정 불가</ReadOnlyChip>}
              </CardHeadActions>
            </CardHeader>

            <MetaRow>
              <Field>
                <Label>버전</Label>
                <Input
                  value={draft.version}
                  readOnly={readOnly}
                  onChange={(e) => patchDraft(t.key, 'version', e.target.value)}
                  placeholder="예: 1.0.0"
                />
              </Field>
              <Field>
                <Label>시행일</Label>
                <Input
                  type="date"
                  value={draft.effective_date}
                  readOnly={readOnly}
                  onChange={(e) => patchDraft(t.key, 'effective_date', e.target.value)}
                />
              </Field>
              <Field>
                <Label>최종 수정</Label>
                <Input value={formatDateTime(current?.updated_at)} readOnly />
              </Field>
            </MetaRow>

            <Field>
              <Label>본문</Label>
              <Body
                value={draft.content}
                readOnly={readOnly}
                onChange={(e) => patchDraft(t.key, 'content', e.target.value)}
                placeholder="본문을 입력하세요"
              />
            </Field>

            {!readOnly && (
              <CardFoot>
                <SaveBtn
                  disabled={savingKey === t.key}
                  onClick={() => handleSaveOne(t)}
                >
                  {savingKey === t.key ? '저장 중...' : '저장'}
                </SaveBtn>
              </CardFoot>
            )}
          </Card>
        );
      })}

      {historyTerm && (
        <Backdrop onClick={closeHistory}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>{historyTerm.label} — 버전 이력</ModalTitle>
                <ModalMeta>저장 직전 버전이 자동 보관됩니다. 이력은 최근 50건.</ModalMeta>
              </div>
              <CloseBtn onClick={closeHistory}>닫기</CloseBtn>
            </ModalHead>

            {historyLoading && <Empty>불러오는 중...</Empty>}
            {!historyLoading && historyRows.length === 0 && (
              <Empty>저장된 이전 버전이 없습니다. (현재 버전은 위 카드에 표시)</Empty>
            )}

            <HistoryList>
              {historyRows.map((row) => {
                const expanded = expandedHistoryId === row.id;
                return (
                  <HistoryItem key={row.id}>
                    <HistoryRowTop onClick={() => setExpandedHistoryId(expanded ? '' : row.id)}>
                      <div>
                        <HistoryVersion>v{row.version || '-'}</HistoryVersion>
                        <HistoryMetaInline>시행일 {row.effective_date || '-'} · 보관 {formatDateTime(row.archived_at)}</HistoryMetaInline>
                      </div>
                      <ExpandToggle>{expanded ? '접기 ▲' : '본문 보기 ▼'}</ExpandToggle>
                    </HistoryRowTop>
                    <HistoryRowMeta>
                      변경자: {row.archived_by_email || row.archived_by_uid || '-'}
                      {row.next_version ? ` · 다음 버전: v${row.next_version}` : ''}
                    </HistoryRowMeta>
                    {expanded && <HistoryBody>{row.content || '(본문 없음)'}</HistoryBody>}
                  </HistoryItem>
                );
              })}
            </HistoryList>
          </Modal>
        </Backdrop>
      )}
    </Wrap>
  );
};

export default TermsSettings;

const Wrap = styled.div``;

const Header = styled.div`
  margin-bottom: 16px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  margin-bottom: 8px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const Desc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ReadOnlyChip = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textLight};
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const SaveBtn = styled.button`
  padding: 10px 26px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
  &:disabled { opacity: 0.6; cursor: default; }
`;

const CardFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const CardHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HistoryBtn = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-weight: 500;
  &:hover { background: ${({ theme }) => theme.colors.border}; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: 86vh;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const ModalMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseBtn = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const HistoryList = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HistoryItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
`;

const HistoryRowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const HistoryVersion = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-right: 10px;
`;

const HistoryMetaInline = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ExpandToggle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
`;

const HistoryRowMeta = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const HistoryBody = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
  max-height: 280px;
  overflow-y: auto;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 180px 180px 1fr;
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: ${({ readOnly, theme }) => (readOnly ? theme.colors.borderLight : '#fff')};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Body = styled.textarea`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.6;
  min-height: 280px;
  resize: vertical;
  font-family: inherit;
  background: ${({ readOnly, theme }) => (readOnly ? theme.colors.borderLight : '#fff')};
  white-space: pre-wrap;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;
